import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2CardBody,
  V2Button,
  V2Badge,
  V2Tag,
  V2Avatar,
  V2Kpi,
  V2MetricStrip,
  V2Table,
  V2ProgressBar,
  V2EmptyState,
  V2CheckRow,
  V2SearchInput,
  V2Alert,
  V2Modal,
  V2ConfirmDialog,
  V2Switch,
  V2Checkbox,
  V2ToastStack,
  V2Skeleton,
  LoadingState,
  StatePanel,
  Icon,
  type V2TableColumn,
  type V2BadgeTone,
  type V2ButtonVariant,
} from '@sakhtar/design-system/react';
import { BarChart, DonutChart, LineChart } from '../../lib/charts/index.js';

const BUTTON_VARIANTS: readonly V2ButtonVariant[] = ['primary', 'secondary', 'ghost', 'danger'];
const BADGE_TONES: readonly V2BadgeTone[] = [
  'neutral',
  'success',
  'warn',
  'danger',
  'info',
  'brand',
];

const BRAND_RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

const SEMANTIC_SWATCHES: readonly { readonly name: string; readonly var: string }[] = [
  { name: 'success', var: '--sk-success' },
  { name: 'warn', var: '--sk-warn' },
  { name: 'danger', var: '--sk-danger' },
  { name: 'info', var: '--sk-info' },
];

const CHART_ORDER_VARS = [
  '--sk-chart-1',
  '--sk-chart-6',
  '--sk-chart-3',
  '--sk-chart-2',
  '--sk-chart-4',
  '--sk-chart-5',
] as const;

const DESIGN_REFERENCES: readonly string[] = [
  'Sakhtar Cloud ERP - Final.dc.html — shell IA (sidebar, topbar, mobile nav)',
  'Sakhtar Pilot P0 — auth family, security console, subscription, master data',
  'Sakhtar Sales CRM.dc.html — CRM hub, leads, opportunities, quotes, orders',
  'Sakhtar Finance.dc.html — accounts, journals, AR/AP, treasury, VAT',
  'Sakhtar Inventory.dc.html — stock, receipts/issues/transfers, adjustments, reservations',
  'Sakhtar Procurement.dc.html — purchase requests, purchase orders, suppliers',
  'Sakhtar Mobile App.dc.html — mobile bottom nav, account/notification/menu sheets',
];

interface SampleRow {
  readonly id: string;
  readonly name: string;
  readonly amountRial: number;
  readonly statusLabel: string;
  readonly tone: V2BadgeTone;
}

const SAMPLE_ROWS: readonly SampleRow[] = [
  { id: '1', name: 'ردیف نمونه ۱', amountRial: 1_250_000, statusLabel: 'ثبت‌شده', tone: 'success' },
  { id: '2', name: 'ردیف نمونه ۲', amountRial: 480_000, statusLabel: 'در انتظار', tone: 'warn' },
  { id: '3', name: 'ردیف نمونه ۳', amountRial: 92_000, statusLabel: 'لغوشده', tone: 'neutral' },
];

function Section({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
}) {
  return (
    <V2Card>
      <V2CardHeader title={title} subtitle={subtitle} />
      <V2CardBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>
      </V2CardBody>
    </V2Card>
  );
}

function Swatch({ label, cssVar }: { readonly label: string; readonly cssVar: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 84 }}>
      <div
        style={{
          height: 44,
          borderRadius: 'var(--sk-r-md)',
          background: `var(${cssVar})`,
          border: '1px solid var(--sk-border)',
        }}
      />
      <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }} className="ltr">
        {label}
      </span>
    </div>
  );
}

/**
 * UI_REFERENCE_SOURCE:
 * section: no single Design Board — a living catalog of every real
 *   @sakhtar/design-system v2 primitive and ChartKit component this
 *   project has actually built against the seven boards listed below,
 *   assembled per explicit product-owner request as an in-app UI
 *   reference (not sourced from one board itself).
 * archetype: STATIC_REFERENCE — no backend endpoint, no tenant data.
 *   Every value shown (row amounts, chart series, KPI numbers) is
 *   illustrative sample data for demonstrating the component, clearly
 *   labeled as such — never presented as this tenant's real numbers.
 * state reference: n/a (static)
 *
 * ACCESS GAP (open, not fixed by this pass): meant for Sakhtar's own
 * operator/super-admin, not tenant users — but apps/client has no
 * operator-identity concept anywhere (that lives only in apps/web's
 * separate SSR super-admin surface, still on v1). Per explicit product
 * direction this stays here rather than moving to apps/web (v2/ChartKit
 * only exist here), reachable at admin/ui-kit with no tenant-sidebar
 * entry — that omission is obscurity, not authorization (AGENTS.md §2.3:
 * "Hiding something in the UI is not a permission"). A real fix needs
 * either a genuine operator-session check plumbed into apps/client, or a
 * dedicated backend permission gate — tracked as follow-up, not built
 * here without that decision.
 */
/** Live examples of the V2 feedback / dialog / toggle primitives (the replacements for v1 Alert, Modal, ConfirmDialog, Switch, Checkbox, Toast, Skeleton). */
function V2FeedbackExamples(): ReactNode {
  const [modal, setModal] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [on, setOn] = useState(true);
  const [checked, setChecked] = useState(false);
  const [toasts, setToasts] = useState<{ id: string; message: string; tone: 'success' }[]>([]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <V2Alert tone="info" title="اطلاع">
        پیام راهنما برای کاربر.
      </V2Alert>
      <V2Alert tone="success" title="انجام شد">
        تغییرات ذخیره شد.
      </V2Alert>
      <V2Alert
        tone="warn"
        title="هشدار"
        action={
          <V2Button size="sm" variant="secondary">
            مشاهده
          </V2Button>
        }
      >
        این کار قابل بازگشت نیست.
      </V2Alert>
      <V2Alert tone="danger" title="خطا" onClose={() => undefined}>
        ثبت انجام نشد؛ دوباره تلاش کنید.
      </V2Alert>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <V2Switch label="اعلان‌ها" description="ارسال پیامک" checked={on} onCheckedChange={setOn} />
        <V2Checkbox label="قوانین را می‌پذیرم" checked={checked} onCheckedChange={setChecked} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <V2Button onClick={() => setModal(true)}>باز کردن پنجره</V2Button>
        <V2Button variant="danger" onClick={() => setConfirm(true)}>
          حذف…
        </V2Button>
        <V2Button
          variant="secondary"
          onClick={() =>
            setToasts([{ id: String(Date.now()), message: 'ذخیره شد', tone: 'success' }])
          }
        >
          نمایش پیام کوتاه
        </V2Button>
      </div>
      <div style={{ display: 'grid', gap: 6, maxWidth: 360 }}>
        <V2Skeleton height={16} />
        <V2Skeleton width="70%" height={16} />
      </div>
      <V2Modal
        open={modal}
        title="عنوان پنجره"
        onClose={() => setModal(false)}
        footer={
          <>
            <V2Button variant="ghost" onClick={() => setModal(false)}>
              انصراف
            </V2Button>
            <V2Button variant="primary" onClick={() => setModal(false)}>
              ثبت
            </V2Button>
          </>
        }
      >
        محتوای پنجره اینجا می‌آید.
      </V2Modal>
      <V2ConfirmDialog
        open={confirm}
        title="حذف رکورد"
        message="این رکورد حذف می‌شود."
        danger
        confirmLabel="حذف"
        onConfirm={() => setConfirm(false)}
        onCancel={() => setConfirm(false)}
      />
      <V2ToastStack
        toasts={toasts}
        onDismiss={(id) => setToasts((t) => t.filter((x) => x.id !== id))}
      />
    </div>
  );
}

export function UiKitPage() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div className="sk-frame-label">/t/{slug} — راهنمای رابط کاربری</div>
        <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
          کیت رابط کاربری ساختار
        </h1>
        <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
          کاتالوگ زندهٔ اجزای طراحی v2 — رنگ‌ها، تایپوگرافی، کامپوننت‌ها و چارت‌ها. این صفحه فقط
          مرجع است؛ داده‌های نمایش‌داده‌شده نمونه‌اند، نه اطلاعات واقعی این سازمان.
        </div>
      </div>

      <Section title="برند" subtitle="لوگو و نام پلتفرم — همان دارایی بالای ساید‌بار">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 'var(--sk-r-md)',
              background: 'var(--sk-brand-700)',
              display: 'grid',
              placeContent: 'center',
            }}
          >
            <Icon name="building" size="md" />
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>ساختار</div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>Sakhtar Cloud ERP</div>
          </div>
        </div>
      </Section>

      <Section
        title="رنگ‌ها"
        subtitle="طیف برند (--sk-brand-50..900)، رنگ‌های معنایی، و ترتیب ثابت چارت‌ها"
      >
        <div>
          <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
            طیف برند (Sakhtar Indigo)
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {BRAND_RAMP_STEPS.map((step) => (
              <Swatch key={step} label={`--sk-brand-${step}`} cssVar={`--sk-brand-${step}`} />
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
            رنگ‌های معنایی (ثابت در هر دو تم)
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {SEMANTIC_SWATCHES.map((s) => (
              <Swatch key={s.name} label={s.var} cssVar={s.var} />
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
            ترتیب ثابت چارت‌ها — بر اساس موقعیت انتساب می‌شود، نه بازچینی (اعتبارسنجی‌شده در برابر
            کوررنگی؛ ببینید packages/design-system/src/tokens/v2/colors.ts)
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {CHART_ORDER_VARS.map((v, i) => (
              <Swatch key={v} label={`#${i + 1} ${v}`} cssVar={v} />
            ))}
          </div>
        </div>
      </Section>

      <Section title="تایپوگرافی" subtitle="مقیاس فونت v2 (--sk-fs-*)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>2xl — عنوان صفحه</div>
          <div style={{ fontSize: 'var(--sk-fs-xl)', fontWeight: 700 }}>xl — عنوان بخش</div>
          <div style={{ fontSize: 'var(--sk-fs-lg)' }}>lg — متن برجسته</div>
          <div style={{ fontSize: 'var(--sk-fs-md)' }}>md — متن پایه</div>
          <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
            sm — متن فرعی
          </div>
          <div style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-subtle)' }}>
            xs — برچسب و متادیتا
          </div>
        </div>
      </Section>

      <Section title="دکمه‌ها" subtitle="V2Button — چهار حالت، سه اندازه">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {BUTTON_VARIANTS.map((variant) => (
            <V2Button key={variant} variant={variant}>
              {variant}
            </V2Button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <V2Button variant="primary" size="sm">
            sm
          </V2Button>
          <V2Button variant="primary" size="md">
            md
          </V2Button>
          <V2Button variant="primary" size="lg">
            lg
          </V2Button>
          <V2Button variant="primary" icon="plus">
            با آیکون
          </V2Button>
          <V2Button variant="secondary" disabled>
            غیرفعال
          </V2Button>
        </div>
      </Section>

      <Section title="نشان‌ها و برچسب‌ها" subtitle="V2Badge (شش تن) و V2Tag">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {BADGE_TONES.map((tone) => (
            <V2Badge key={tone} tone={tone}>
              {tone}
            </V2Badge>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <V2Tag>برچسب نمونه</V2Tag>
          <V2Avatar initials="ک" />
          <V2Avatar initials="کس" size="lg" />
        </div>
      </Section>

      <Section title="فرم و کنترل‌ها" subtitle="جست‌وجو، نوار پیشرفت، ردیف انتخاب">
        <V2SearchInput placeholder="جست‌وجوی نمونه" value="" onChange={() => {}} width={240} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
          <V2ProgressBar percent={35} />
          <V2ProgressBar percent={70} />
        </div>
        <V2CheckRow label="گزینهٔ نمونه" meta="متادیتا" checked onChange={() => {}} />
      </Section>

      <Section title="KPI و نوار متریک" subtitle="V2Kpi در V2MetricStrip">
        <V2MetricStrip>
          <V2Kpi label="نمونه شاخص ۱" value="۱۲۴" delta="+۸٪" deltaDirection="up" />
          <V2Kpi label="نمونه شاخص ۲" value="۳٬۴۵۰٬۰۰۰ ریال" />
          <V2Kpi label="نمونه شاخص ۳" value="۹" deltaDirection="down" delta="-۲" />
        </V2MetricStrip>
      </Section>

      <Section title="جدول" subtitle="V2Table با ردیف‌های نمونه">
        <V2Table
          columns={
            [
              { key: 'name', header: 'نام', render: (r) => r.name },
              {
                key: 'amount',
                header: 'مبلغ (ریال)',
                align: 'end',
                render: (r) => (
                  <span className="sk-num">{r.amountRial.toLocaleString('fa-IR')}</span>
                ),
              },
              {
                key: 'status',
                header: 'وضعیت',
                render: (r) => <V2Badge tone={r.tone}>{r.statusLabel}</V2Badge>,
              },
            ] satisfies readonly V2TableColumn<SampleRow>[]
          }
          rows={SAMPLE_ROWS}
          zebra
        />
      </Section>

      <Section
        title="چارت‌ها (ChartKit)"
        subtitle="BarChart / DonutChart / LineChart — موتور ECharts، داده‌های نمونه"
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              BarChart
            </div>
            <BarChart
              ariaLabel="نمونهٔ نمودار میله‌ای"
              data={[
                { label: 'فروردین', value: 12 },
                { label: 'اردیبهشت', value: 18 },
                { label: 'خرداد', value: 9 },
                { label: 'تیر', value: 24 },
              ]}
              height={220}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              DonutChart
            </div>
            <DonutChart
              ariaLabel="نمونهٔ نمودار دونات"
              centerLabel="کل نمونه"
              data={[
                { label: 'باز', value: 14 },
                { label: 'در جریان', value: 8 },
                { label: 'بسته', value: 22 },
              ]}
              height={220}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              LineChart
            </div>
            <LineChart
              ariaLabel="نمونهٔ نمودار خطی"
              categories={['هفته ۱', 'هفته ۲', 'هفته ۳', 'هفته ۴']}
              series={[
                { name: 'سری الف', data: [5, 9, 7, 12] },
                { name: 'سری ب', data: [3, 4, 8, 6] },
              ]}
              height={220}
            />
          </div>
        </div>
      </Section>

      <Section title="حالت‌های صفحه" subtitle="بارگذاری، خالی، خطا/دسترسی">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              LoadingState
            </div>
            <LoadingState />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              V2EmptyState
            </div>
            <V2EmptyState icon="info" title="موردی برای نمایش نیست" />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 8 }}>
              StatePanel — network_error
            </div>
            <StatePanel variant="network_error" title="اطلاعات قابل نمایش نیست" />
          </div>
        </div>
      </Section>

      <Section
        title="پیام، پنجره و کلید"
        subtitle="V2Alert · V2Modal · V2ConfirmDialog · V2Switch · V2Checkbox · V2ToastStack · V2Skeleton"
      >
        <V2FeedbackExamples />
      </Section>

      <Section
        title="منابع طراحی"
        subtitle="بردهای Claude Design که کل رابط کاربری از روی آن‌ها ساخته شده"
      >
        <ul
          style={{
            margin: 0,
            paddingInlineStart: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {DESIGN_REFERENCES.map((ref) => (
            <li key={ref} style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              {ref}
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
